import { Injectable, OnDestroy } from "@angular/core";
import { ConfigPermission } from "@docma-mm-core/permission/config-permission";
import { ProjectActionPermissionEnum } from "@docma-mm-core/permission/project-action-permission.enum";
import { ProjectUserPermissions } from "@docma-mm-core/permission/project-user-permissions";
import { Project as MmProject } from "@docma-mm-core/project/project";
import { ConnectivityService } from "@mydocma/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { filter, first, map, switchMap, tap } from "rxjs/operators";
import { MmProjectOfflineService } from "./mm-project-offline.service";
import { MmProjectService } from "./mm-project.service";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {LoadStatusEnum} from "@docma-mm-core/model/load-state/load-state.enum";
import {ViewPoint} from "@docma-mm-core/status/viewpoint.enum";
import {MmProjectLoaderService} from "./mm-project-loader.service";
import {ActionPermissionEnum} from "@docma-mm-core/permission/action-permission.enum";
import { ConfigPermissionEnum } from "@docma-mm-core/permission/config-permission.enum";
import { DataAccessPermissionEnum } from "@docma-mm-core/permission/data-access-permission.enum";
import { mmRoutes } from "../mm-routing/mm-routes";
import { MmRoutesEnum } from "../mm-routing/mm-routes.enum";
import { routeToPath } from "../mm-routing/mm-routes.map";

@UntilDestroy()
@Injectable()
export class MmProjectPermissionsService implements OnDestroy {

  private projectPermissionsSubject = new BehaviorSubject<ProjectUserPermissions>(null);

  public permissions$ = this.projectPermissionsSubject.asObservable();

  public permissionNewActivity$ = this.hasProjectActionPermissions([ProjectActionPermissionEnum.NewActivity]);
  public permissionViewActivitiesCO$ = this.hasProjectActionPermissions([ProjectActionPermissionEnum.ViewActivitiesCO]);
  public permissionViewActivitiesCT$ = this.hasProjectActionPermissions([ProjectActionPermissionEnum.ViewActivitiesCT]);

  public hasShortcuts$ = this.hasShortcuts();

  public shortcuts$ = this.permissions$
    .pipe(
      switchMap(p => this.projectLoaderService.statuses.pipe(
        switchMap(st => st.loadState$.pipe(filter(ls => ls.status == LoadStatusEnum.Loaded)).pipe(
          switchMap(status => this.permissionViewActivitiesCO$.pipe(
            switchMap(permActivitiesCO => this.permissionViewActivitiesCT$.pipe(
              map(permActivitiesCT => p.Shortcuts
                ?.filter(f =>
                  ((f.Status.ViewPoint === ViewPoint.Contractor && permActivitiesCO)
                    || (f.Status.ViewPoint === ViewPoint.Customer && permActivitiesCT))
                  && status.data.findIndex(f2 => f2.Id == f.Status.Id) !== -1
                )
                ?.sort(x => x.Symbol) ?? []
              )
              // , tap(x => {
              //   console.info('' + JSON.stringify(x));
              // })
            ))
          ))
        ))
      ))
      , map(x => x)
    );

  constructor(private readonly projectService: MmProjectService,
              private readonly connectivityService: ConnectivityService,
              private readonly offlineService: MmProjectOfflineService,
              private readonly projectLoaderService: MmProjectLoaderService) {

    this.projectService.currentProject
      .pipe(
        untilDestroyed(this),
        tap(p => !p && this.projectPermissionsSubject.next(null)),
        switchMap(p => !p ? of(p) :
          this.offlineService.getPermissions(p, false)
            .pipe(
              tap(ps => this.notifyPermissionsChange(p, ps)),
              map(ps => p)
            )
        ),
        switchMap(p => this.connectivityService.appIsInOfflineMode
          .pipe(
            filter(o => !o),
            map(o => p)
          )
        ),
        switchMap(p => !p ? of(null) :
          this.offlineService.getPermissions(p, true)
            .pipe(
              tap(ps => this.notifyPermissionsChange(p, ps)),
              map(ps => ps)
            )

        )
      )
      .subscribe();
  }

  ngOnDestroy(): void {
  }

  private notifyPermissionsChange(p: MmProject, ps: ProjectUserPermissions) {
    if (!ps) {
      ps = {};
    }
    ps.Project = p;
    this.projectPermissionsSubject.next(ps)
  }
  public hasRoutePermissions(permissions: ProjectUserPermissions, route: string): boolean {
     const idToPermissionMap = new Map<string, ConfigPermissionEnum>([
      [MmRoutesEnum.project_companies, ConfigPermissionEnum.ProjectCompany],
      [MmRoutesEnum.project_crafts, ConfigPermissionEnum.ProjectCraft],
      [MmRoutesEnum.project_variables, ConfigPermissionEnum.ProjectVariables],
      [MmRoutesEnum.project_type1, ConfigPermissionEnum.ProjectUserDefined1],
      [MmRoutesEnum.project_type1, ConfigPermissionEnum.ProjectUserDefined2],
      [routeToPath.get(MmRoutesEnum.project_companies), ConfigPermissionEnum.ProjectCompany],
      [routeToPath.get(MmRoutesEnum.project_crafts), ConfigPermissionEnum.ProjectCraft],
      [routeToPath.get(MmRoutesEnum.project_variables), ConfigPermissionEnum.ProjectVariables],
      [routeToPath.get(MmRoutesEnum.project_type1), ConfigPermissionEnum.ProjectUserDefined1],
      [routeToPath.get(MmRoutesEnum.project_type2), ConfigPermissionEnum.ProjectUserDefined2],
    ]);

    const routePermission = idToPermissionMap.get(route);
    if (!routePermission) {
      return true;
    }
    const hasPermission =  permissions.Configs.findIndex(cp => cp.Config == routePermission &&
      ((cp.Permission & DataAccessPermissionEnum.Read) === DataAccessPermissionEnum.Read || (cp.Permission & DataAccessPermissionEnum.Write) === DataAccessPermissionEnum.Write)) != -1;

    return hasPermission;
  }

  public hasConfigAccessPermission(permissions: ProjectUserPermissions, configPermission: ConfigPermission): boolean {
    if (!permissions || !permissions.Configs) {
      return false;
    }

    const foundPermission = permissions.Configs.find(c => c.Config === configPermission.Config);

    if (!foundPermission || !foundPermission.Permission) {
      return false;
    }

    if ((foundPermission.Permission & configPermission.Permission) === configPermission.Permission) {
        return true;
    }

    return false;
  }

  public hasProjectActionPermission(permissions: ProjectUserPermissions, action: ProjectActionPermissionEnum): boolean {
    if (!permissions || !permissions.Actions) {
      return false;
    }
    const found = permissions.Actions?.find(a => a === action);
    return !!found;
  }

  public hasActionPermission(permissions: ProjectUserPermissions, action: ActionPermissionEnum): boolean {
    if (!permissions || !permissions.Actions) {
      return false;
    }
    const found = permissions.AdminActions?.find(a => a === action);
    return !!found;
  }

  public hasProjectConfigPermissions(permissions: ConfigPermission[]): Observable<boolean> {
    if (!permissions || permissions.length === 0) {
      return of(true);
    }
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),
              first()
            )
        ),
        map(userPermissions => {
          for (const perm of permissions) {
            if (!this.hasConfigAccessPermission(userPermissions,perm)) {
              return false;
            }
          }
          return true;
        })
      );
  }

  public hasProjectActionPermissions(actions: ProjectActionPermissionEnum[]): Observable<boolean> {
    if (!actions || actions.length === 0) {
      return of(true);
    }
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),

            )
        ),
        map(userPermissions => {
          for (const a of actions) {
            if (!this.hasProjectActionPermission(userPermissions,a)) {
              return false;
            }
          }
          return true;
        })
      );
  }

  public hasActionPermissions(actions: ActionPermissionEnum[]): Observable<boolean> {
    if (!actions || actions.length === 0) {
      return of(true);
    }
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),

            )
        ),
        map(userPermissions => {
          for (const a of actions) {
            if (!this.hasActionPermission(userPermissions, a)) {
              return false;
            }
          }
          return true;
        })
      );
  }

  public hasShortcuts(): Observable<boolean> {
    return this.projectService.currentProject
      .pipe(
        switchMap(project =>
          this.permissions$
            .pipe(
              filter(ps => !project || (ps && ps.Project && ps.Project.Id === project.Id)),
            )
        ),
        map(userPermissions => {
          if (!userPermissions) {
            return false;
          }
          return ((userPermissions.Shortcuts?.length ?? 0) > 0);
        })
      );
  }
}
